import {
  Autocomplete,
  Box,
  Dialog,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { Education } from "../../model/surveyor_filter_model";
import { Option } from "../../../needs/model/needs_filters";
import { useQuery } from "react-query";
import { getDistrictMasterData } from "../../../../services/master/master_service";
import { useSurveyorResponsesFilterStore } from "../../../../store/use-surveyors-responses-filter-store";
import {
  FilterDialogFooter,
  FilterDialogHeader,
  PillToggleButton,
} from "../../../users/components/filter_dialog/filter_dialog_parts";

interface FilterEnquiriesInterface {
  states: Education[];
  genderList: string[];
  occupation: Education[];
  education: Education[];
  income: Education[];
  cities: Option[] | [];
}

export default function FilterSubmittedChoiceList({
  states,
  genderList,
  occupation,
  education,
  income,
  cities,
}: FilterEnquiriesInterface) {
  const {
    openModel,
    setOpenModel,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    stateId,
    setStateId,
    categoryId,
    setCategoryId,
    occupationId,
    setOccupationId,
    educationId,
    setEducationId,
    incomeId,
    setIncomeId,
    selectedGender,
    setSelectedGender,
    cityId,
    setCityId,
    districtId,
    setDistrictId,
    resetFilters,
    setApplyFilter
  } = useSurveyorResponsesFilterStore();

  // ✅ District API
  const { data: district } = useQuery(["districtList", stateId], async () => {
    if (stateId) {
      let response = await getDistrictMasterData(stateId);
      return response?.data?.data?.list;
    }
  });

  const handleReset = () => {
    resetFilters();
    setOpenModel(false);
  };

  return (
    <Dialog open={openModel} onClose={() => setOpenModel(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ width: 400, p: 2 }}>
        <FilterDialogHeader title="Filter Surveys" onClose={() => setOpenModel(false)} onReset={handleReset} />
        <Box sx={{ maxHeight: 400, overflow: "auto scroll" }}>
            {/* Gender Filter */}
            <Typography fontSize={16} fontWeight="600" mb={1}>
              Gender
            </Typography>
            <Box>
              {genderList?.map((gender) => (
                <PillToggleButton
                  key={gender}
                  label={gender}
                  active={selectedGender === gender}
                  onClick={() => setSelectedGender(selectedGender === gender ? "" : gender)}
                />
              ))}
            </Box>

            <Divider sx={{ mt: 2, mb: 1 }} />

            {/* Dropdowns */}
            {_dropdownOption({
              title: "State",
              placeholder: "Search for State",
              options: states,
              selectedId: stateId,
              onSelect: setStateId,
            })}
            {_dropdownOption({
              title: "District",
              placeholder: "Select District",
              selectedId: districtId,
              onSelect: setDistrictId,
              options:
                district?.map((city: any) => ({
                  id: city.id,
                  name: city?.value,
                })) || [],
            })}
            {_dropdownOption({
              title: "Filter by Occupation",
              placeholder: "Search for Occupation",
              options: occupation,
              selectedId: occupationId,
              onSelect: setOccupationId,
            })}
            {_dropdownOption({
              title: "Filter by Education",
              placeholder: "Search for Education",
              options: education,
              selectedId: educationId,
              onSelect: setEducationId,
            })}
            {_dropdownOption({
              title: "Filter by Personal Income",
              placeholder: "Search for Personal Income",
              options: income,
              selectedId: incomeId,
              onSelect: setIncomeId,
            })}

            {/* Date Range */}
            {_dateRange({
              title: "Date Range",
              setEndDate,
              setStartDate,
              startDate,
              endDate,
            })}

            {/* Footer */}
            <FilterDialogFooter
              onCancel={() => setOpenModel(false)}
              onApply={() => {
                setOpenModel(false);
                setApplyFilter(true);
              }}
            />
        </Box>
      </Box>
    </Dialog>
  );

  // 🔽 Dropdown Helper
  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; name: string }[];
    selectedId: number | null;
    onSelect: (id: number | null) => void;
  }) {
    return (
      <Box pt={2} pb={1}>
        <Typography fontSize={16} fontWeight="600">
          {title}
        </Typography>
        <Box pt={1} pb={2} display="flex" flexDirection="row">
          <Autocomplete
            fullWidth
            value={options?.find((option) => selectedId === option?.id) || null}
            onChange={(e, value) => {
              onSelect(value?.id || null);
            }}
            options={options}
            getOptionLabel={(option) => option?.name}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={placeholder}
                placeholder={placeholder}
              />
            )}
          />
        </Box>
        <Divider />
      </Box>
    );
  }

  // 🔽 Date Range Helper
  function _dateRange({
    title,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  }: {
    title: string;
    startDate: string;
    setStartDate: (value: string) => void;
    endDate: string;
    setEndDate: (value: string) => void;
  }) {
    return (
      <Box pt={2} pb={1}>
        <Typography fontSize={16} fontWeight="600">
          {title}
        </Typography>
        <Box pt={1} pb={2} display="flex" flexDirection={"column"}>
          <DatePicker
            label="Start Date"
            format="DD-MM-YYYY"
            disableFuture
            value={startDate ? dayjs(startDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setStartDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: { elevation: 3 } }}
            sx={{
              "& .MuiSvgIcon-root": {
                fill: "#990099",
              },
            }}
          />
          <Box p={1}></Box>
          <DatePicker
            label="End Date"
            format="DD-MM-YYYY"
            disableFuture
            value={endDate ? dayjs(endDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setEndDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: { elevation: 3 } }}
            sx={{
              "& .MuiSvgIcon-root": {
                fill: "#990099",
              },
            }}
          />
        </Box>
      </Box>
    );
  }
}
